import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type { ZodSchema } from 'zod';

import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function jsonValueEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!jsonValueEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const keysA = Object.keys(aObj);
  if (keysA.length !== Object.keys(bObj).length) return false;
  for (const k of keysA) {
    if (!Object.hasOwn(bObj, k)) return false;
    if (!jsonValueEqual(aObj[k], bObj[k])) return false;
  }
  return true;
}

function bufferStringFromValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return JSON.stringify(value, null, 2);
}

export type JsonEditorHandle = {
  validate: () => boolean;
};

export type JsonEditorProps = {
  value: unknown;
  onChange: (value: unknown) => void;
  onErrorsChange?: (hasErrors: boolean) => void;
  schema?: ZodSchema;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
};

export const JsonEditor = forwardRef<JsonEditorHandle, JsonEditorProps>(function JsonEditor(
  {
    value,
    onChange,
    onErrorsChange,
    schema,
    label: labelText,
    helperText,
    disabled,
    required,
    id,
    name,
  },
  ref
) {
  const [buffer, setBuffer] = useState(() => bufferStringFromValue(value));
  const [errors, setErrors] = useState<string[]>([]);
  const lastParentValue = useRef(value);
  const autoId = useId();
  const fieldId = id ?? autoId;
  const helperId = `${fieldId}-helper`;
  const errorsId = `${fieldId}-errors`;

  const bufferRef = useRef(buffer);
  bufferRef.current = buffer;

  const runValidation = useCallback(
    (raw: string): string[] => {
      if (raw.length === 0) {
        setErrors([]);
        onErrorsChange?.(required ? true : false);
        onChange(null);
        return [];
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        const trimmed = msg.trim().slice(0, 80);
        const err = `JSON: ${trimmed}`;
        setErrors([err]);
        onErrorsChange?.(true);
        return [err];
      }
      if (schema === undefined) {
        setErrors([]);
        onErrorsChange?.(false);
        onChange(parsed);
        return [];
      }
      const result = schema.safeParse(parsed);
      if (!result.success) {
        const list = result.error.issues.map(
          (i) => `${i.path.join('.') || '(root)'}: ${i.message}`
        );
        setErrors(list);
        onErrorsChange?.(true);
        return list;
      }
      setErrors([]);
      onErrorsChange?.(false);
      onChange(result.data);
      return [];
    },
    [onChange, onErrorsChange, required, schema]
  );

  const runValidationRef = useRef(runValidation);
  runValidationRef.current = runValidation;

  const onErrorsChangeRef = useRef(onErrorsChange);
  onErrorsChangeRef.current = onErrorsChange;

  useEffect(() => {
    if (required && bufferRef.current.length === 0) {
      onErrorsChangeRef.current?.(true);
    }
    // intentional: runs once on mount to set initial disabled state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const e = runValidationRef.current(bufferRef.current);
        return e.length === 0;
      },
    }),
    []
  );

  useEffect(() => {
    let parsed: unknown = null;
    if (bufferRef.current.length > 0) {
      try {
        parsed = JSON.parse(bufferRef.current);
      } catch {
        return;
      }
    }
    if (!jsonValueEqual(parsed, lastParentValue.current)) {
      return;
    }
    const nextString = bufferStringFromValue(value);
    setBuffer(nextString);
    lastParentValue.current = value;
    setErrors([]);
  }, [value]);

  const onInputChange = (raw: string) => {
    setBuffer(raw);
    runValidation(raw);
  };

  const onBlur = () => {
    runValidation(bufferRef.current);
  };

  const describedBy = [helperText ? helperId : null, errors.length > 0 ? errorsId : null]
    .filter((x) => x !== null)
    .join(' ');

  return (
    <div className="flex flex-col gap-2">
      {labelText ? <Label htmlFor={fieldId}>{labelText}</Label> : null}
      {helperText ? (
        <p className="text-sm text-muted-foreground" id={helperId}>
          {helperText}
        </p>
      ) : null}
      <Textarea
        aria-label={labelText ? undefined : 'config_json'}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        aria-invalid={errors.length > 0}
        className="font-mono w-full"
        disabled={Boolean(disabled)}
        id={fieldId}
        name={name}
        onBlur={onBlur}
        onChange={(e) => onInputChange(e.target.value)}
        rows={10}
        value={buffer}
      />
      {errors.length > 0 ? (
        <div className="mt-2" id={errorsId}>
          {errors.map((line, index) => (
            <p className="text-sm text-destructive" key={`${index}-${line}`}>
              {line}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
});

JsonEditor.displayName = 'JsonEditor';
